<template>
  <v-container id="credential_certificates" fluid>
    <!-- CONTENT -->
    <v-row class="mb-6" align="center" justify="start">
      <v-col>
        <span class="text-h5">
          {{ $t('credentials.certificates.title.listForX', { name: credential.description }) }}
        </span>
      </v-col>
    </v-row>

    <v-row class="mb-4" align="center" justify="start">
      <!-- downloadCertReq -->
      <v-btn id="download_csr" class="mx-3" color="primary" @click="downloadCertificateRequestToFile()">
        <v-icon left>mdi-cloud-download</v-icon>
        {{ $t('credentials.certificates.content.downloadCsr') }}
      </v-btn>

      <v-tooltip :disabled="isEligible" top>
        <template #activator="{ on }">
          <div v-on="on">
            <v-btn v-show="!certificatesInEditMode" id="edit_certificates" :disabled="!isEligible" @click="openCertificateEditorMode()">
              <v-icon left>mdi-pencil</v-icon>
              {{ $t('credentials.certificates.content.editMode') }}
            </v-btn>
          </div>
        </template>
        <span>{{ $t('credentials.content.createCredentialDisabledTooltip') }}</span>
      </v-tooltip>

      <div v-show="certificatesInEditMode">
        <!-- loadSignerCertificate -->
        <input
          id="upload_certificate"
          ref="certificateUpload"
          style="display: none"
          type="file"
          multiple
          accept=".pem, .cer, .crt"
          @change="loadCertificatesFromFile($event)"
        >
        <v-btn @click="$refs.certificateUpload.click()">
          <v-icon left>mdi-plus</v-icon>
          {{ $t('credentials.certificates.content.uploadCertificate') }}
        </v-btn>

        <!-- replaceCertificate(s) -->
        <v-btn id="save_certificates" @click="replaceCertificateChain()">
          <v-icon left>mdi-cloud-upload</v-icon>
          {{ $t('credentials.certificates.content.saveCertificates') }}
        </v-btn>

        <!-- cancel -->
        <v-btn @click="closeCertificateEditorMode()">
          <v-icon left>mdi-cancel</v-icon>
          {{ $t('main.cancel') }}
        </v-btn>
      </div>
    </v-row>

    <v-card>
      <v-row>
        <v-col>
          <v-data-table
            :headers="[ { text: $t('dataTable.header.credentials.certificates.ranking'), align: 'left', value: 'ranking', sortable: false }]"
            :items="credential.certChain"
            :no-data-text="$t('dataTable.content.noDataAvailable')"
            :hide-default-footer="true"
          >
            <template #[`item.ranking`]="{ index }">
              <span>{{ calculateCertificateRanking(index) }}</span>
            </template>
          </v-data-table>
        </v-col>
        <v-col cols="10">
          <v-data-table
            id="credentials_table"
            v-sortable-data-table
            :headers="headers"
            :items="credential.certChain"
            :no-data-text="$t('dataTable.content.noDataAvailable')"
            :hide-default-footer="true"
            @sorted="dragReorder"
          >
            <template #[`item.issuedTo`]="{ item }">
              <long-text id="issued_to" :text="item.issuedTo" />
            </template>
            <template #[`item.issuedBy`]="{ item }">
              <long-text id="issued_by" :text="item.issuedBy" />
            </template>
            <template #[`item.validFrom`]="{ item }">
              <span class="font-italic caption">{{ convertDate(item.validFrom) }}</span>
            </template>
            <template #[`item.validTo`]="{ item }">
              <span class="font-italic caption">{{ convertDate(item.validTo) }}</span>
            </template>
            <template #[`item.serialNum`]="{ item }">
              <long-text :text="item.serialNum" />
            </template>
            <template #[`item.actions`]="{ item, index }">
              <v-tooltip left>
                <template #activator="{ on }">
                  <v-btn id="download" text class="pa-1 " icon v-on="on" @click="downloadCertificateToFile(item)">
                    <v-icon>mdi-cloud-download</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('dataTable.content.action.credentials.certificates.download') }}</span>
              </v-tooltip>
              <v-tooltip left>
                <template #activator="{ on }">
                  <v-btn v-show="certificatesInEditMode" class="pa-1 " icon v-on="on" @click="deleteCertificateFromCertChainList(index)">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('dataTable.content.action.delete') }}</span>
              </v-tooltip>
              <v-icon v-if="certificatesInEditMode" class="sortHandle">mdi-reorder-horizontal</v-icon>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
      <v-card-actions>
        <v-btn id="back" @click="back">{{ $t('main.back') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import credentials from '../../services/api/credentials'
import Role from '../../Role'
import LongText from '../../components/LongText.vue'
import FileSaver from 'file-saver'
import Sortable from 'sortablejs'
import { showToastSuccess, showToastError } from '../../event-bus'

export default {
  name: 'CredentialCertificates',

  components: {
    LongText
  },

  directives: {
    sortableDataTable: {
      bind(el, binding, vnode) {
        const options = {
          handle: '.sortHandle',
          animation: 150,
          onUpdate: function(event) {
            vnode.child.$emit('sorted', event)
          }
        }
        Sortable.create(el.getElementsByTagName('tbody')[0], options)
      }
    }
  },

  data() {
    return {
      credential: {},
      certificatesInEditMode: false,
      headers: [
        {
          text: this.$t('dataTable.header.credentials.certificates.issuedTo'),
          align: 'left',
          value: 'issuedTo',
          sortable: false
        },
        {
          text: this.$t('dataTable.header.credentials.certificates.issuedBy'),
          align: 'left',
          value: 'issuedBy',
          sortable: false
        },
        {
          text: this.$t('dataTable.header.credentials.certificates.validFrom'),
          align: 'left',
          value: 'validFrom',
          sortable: false
        },
        {
          text: this.$t('dataTable.header.credentials.certificates.validTo'),
          align: 'left',
          value: 'validTo',
          sortable: false
        },
        {
          text: this.$t('dataTable.header.credentials.certificates.serialNum'),
          align: 'left',
          value: 'serialNum',
          sortable: false
        },
        {
          text: this.$t('dataTable.header.actions'),
          align: 'right',
          value: 'actions',
          sortable: false
        }
      ]
    }
  },

  computed: {
    ...mapGetters(['hasRole']),
    isAdmin() {
      return this.hasRole(Role.ADMIN)
    },
    isEligible() {
      return this.$store.state.remoteConfig.adminOnlyCredentialManagement ? this.isAdmin : true
    }
  },

  mounted() {
    this.getCredential()
  },

  methods: {
    async getCredential() {
      try {
        this.credential = await credentials.getCredential(this.$route.params.credentialId)
        this.setCertificatesWithIndex()
      } catch (error) {
        showToastError(this.getErrorMessage(error))
      }
    },

    async downloadCertificateRequestToFile() {
      await FileSaver.saveAs(new Blob([this.credential.p10], { type: 'application/x-pem-file' }), this.credential.description + '-certReq.pem')
    },

    async downloadCertificateToFile(data) {
      await FileSaver.saveAs(new Blob([data.pem], { type: 'application/x-x509-user-cert;charset=utf-8' }), this.credential.description + '-to-' + data.issuedTo + '-by-' + data.issuedBy + '-cert.crt')
    },

    openCertificateEditorMode() {
      this.certificatesInEditMode = true
    },

    closeCertificateEditorMode() {
      this.getCredential()
      this.$refs.certificateUpload.value = null
      this.certificatesInEditMode = false
    },

    loadCertificatesFromFile(event) {
      for (let i = 0; i < event.target.files.length; i++) {
        const file = event.target.files[i]
        const reader = new FileReader()
        reader.onload = event => this.addCertificate(event.target.result)
        reader.readAsDataURL(file)
      }
      this.$refs.certificateUpload.value = null
    },

    async addCertificate(dataURL) {
      var base64Data = dataURL.substr(dataURL.indexOf(',') + 1)
      var certificateInfo = await this.getCertificateInfo(base64Data)
      this.credential.certChain.push(Object.assign({}, certificateInfo, { pem: certificateInfo.pem, id: this.credential.certChain.length }))
    },

    async getCertificateInfo(pem) {
      try {
        return await credentials.getCertificateInfo(pem)
      } catch (error) {
        showToastError(this.getErrorMessage(error))
      }
    },

    async deleteCertificateFromCertChainList(index) {
      this.credential.certChain.splice(index, 1)
    },

    async replaceCertificateChain() {
      const certChain = []
      this.credential.certChain.forEach(certificate => {
        certChain.push(certificate.pem)
      })

      try {
        await credentials.replaceCertificateChain(this.$route.params.credentialId, certChain)
        showToastSuccess(this.$t('toastMessage.succesfullyUploaded'))
      } catch (error) {
        showToastError(this.getErrorMessage(error))
      } finally {
        this.closeCertificateEditorMode()
        this.getCredential()
      }
    },

    dragReorder(event) {
      const movedItem = this.credential.certChain.splice(event.oldIndex, 1)[0]
      this.credential.certChain.splice(event.newIndex, 0, movedItem)
    },

    setCertificatesWithIndex() {
      this.credential.certChain = this.credential.certChain.map(
        (items, index) => ({
          ...items,
          id: index
        }))
    },

    calculateCertificateRanking(index) {
      switch (index) {
        case 0:
          return this.$t('dataTable.content.credentials.certificates.ranking.endUser')
        case this.credential.certChain.length - 1:
          return this.$t('dataTable.content.credentials.certificates.ranking.root')
        default:
          return this.$t('dataTable.content.credentials.certificates.ranking.intermediate') + ' ' + index
      }
    },

    back() {
      this.$router.go(-1)
    }
  }
}
</script>

<style>
.sortHandle {
  cursor: move !important;
  cursor: -webkit-grabbing !important;
}
</style>
